import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import NotificationBell from '@/components/NotificationBell';
import {
  LayoutDashboard, Wrench, Building2, ShieldCheck,
  FileText, Users, LogOut, ChevronRight, ChevronDown, MessageCircle,
  ClipboardList, Store, Shield, Receipt, Sheet, Upload, TrendingUp, Radio, User, FilePlus2, BarChart3, History, HeartPulse, Settings, Target, MapPin, GitBranch
} from 'lucide-react';

const NAV_GROUPS = [
  {
    label: 'Operations',
    items: [
      { to: '/ops/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/ops/repairs', icon: Wrench, label: 'Repairs' },
      { to: '/ops/chat', icon: MessageCircle, label: 'Live Chat' },
    ],
  },
  {
    label: 'Sales',
    items: [
      { to: '/ops/sales', icon: Target, label: 'My Leads' },
      { to: '/ops/intake-inbox', icon: ClipboardList, label: 'Intake Inbox' },
      { to: '/ops/pipeline', icon: GitBranch, label: 'Pipeline' },
    ],
  },
  {
    label: 'Fleet',
    items: [
      { to: '/ops/customers', icon: Building2, label: 'Customers' },
      { to: '/ops/vendors', icon: Store, label: 'Vendors' },
      { to: '/ops/vendor-map', icon: MapPin, label: 'Vendor Map' },
      { to: '/ops/sheets-sync', icon: Sheet, label: 'Sheets Sync' },
      { to: '/ops/import-center', icon: Upload, label: 'Import Center' },
      { to: '/ops/pm-dot', icon: ShieldCheck, label: 'PM / DOT' },
    ],
  },
  {
    label: 'Accounting',
    items: [
      { to: '/ops/invoices', icon: FileText, label: 'Invoices' },
      { to: '/ops/invoice-transformer', icon: Receipt, label: 'Invoice Generator' },
      { to: '/ops/create-invoice', icon: FilePlus2, label: 'Create Invoice' },
      { to: '/ops/account-health', icon: HeartPulse, label: 'Account Health' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/ops/statistics', icon: BarChart3, label: 'Statistics' },
      { to: '/ops/analytics/benchmarking', icon: TrendingUp, label: 'Benchmarking' },
      { to: '/ops/audit-history', icon: History, label: 'Audit History', roles: ['master_admin', 'admin'] },
      { to: '/ops/users', icon: Users, label: 'Users', roles: ['master_admin', 'admin'] },
      { to: '/ops/security', icon: Shield, label: 'Security', roles: ['master_admin', 'admin'] },
      { to: '/ops/settings', icon: Settings, label: 'Settings', roles: ['master_admin', 'admin'] },
      { to: '/ops/settings/repair-categories', icon: Wrench, label: 'Repair Categories', roles: ['master_admin', 'admin'] },
    ],
  },
];

export default function OpsLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const [time, setTime] = useState(new Date());
  
  // Track which sections are expanded (all closed by default)
  const [expandedSections, setExpandedSections] = useState({
    Operations: false,
    Sales: false,
    Fleet: false,
    Accounting: false,
    Admin: false,
  });

  const toggleSection = (label) => {
    setExpandedSections(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  // Auto-expand the section that contains the current route
  useEffect(() => {
    const currentPath = location.pathname;
    NAV_GROUPS.forEach(group => {
      const hasActiveItem = group.items.some(item => currentPath.startsWith(item.to));
      if (hasActiveItem) {
        setExpandedSections(prev => ({
          ...prev,
          [group.label]: true
        }));
      }
    });
  }, [location.pathname]);

  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

  // Prevent horizontal scroll on PWA/mobile
  useEffect(() => {
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    return () => {
      document.body.style.overflowX = '';
      document.documentElement.style.overflowX = '';
    };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => setTime(new Date()), 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) setMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const filteredGroups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.items.filter(i => !i.roles || i.roles.includes(user?.role))
  })).filter(g => g.items.length > 0);

  const currentPage = filteredGroups.flatMap(g => g.items).find(i => location.pathname.startsWith(i.to))?.label || 'Ops';

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--bg)' }}>
      
      {/* ═══ MAIN AREA (comes first = left side) ═══ */}
      <div className="flex-1 flex flex-col overflow-hidden">
        
        {/* Desktop header */}
        <header className="hidden md:flex h-16 items-center justify-between px-6 flex-shrink-0 border-b"
          style={{ background: 'var(--bg-darker)', borderColor: 'var(--border)', boxShadow: '0 1px 0 var(--border), 0 4px 20px rgba(0,0,0,0.45)' }}>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <Logo size={32} />
              <span className="font-['Manrope'] font-extrabold text-sm" style={{ color: 'var(--text-primary)' }}>Millennials</span>
              <span className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--primary)' }}>Trucking</span>
            </div>
            <div className="flex items-center gap-3 pl-6 border-l" style={{ borderColor: 'var(--border)' }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: 'var(--primary-muted)', border: '2px solid var(--primary)' }}>
                <span className="text-sm font-extrabold font-['Manrope']" style={{ color: 'var(--primary)' }}>{initials}</span>
              </div>
              <div>
                <p className="text-sm font-bold truncate max-w-[180px] font-['Manrope']" style={{ color: 'var(--text-primary)' }}>{user?.full_name}</p>
                <p className="text-[10px] font-['JetBrains_Mono'] capitalize" style={{ color: 'var(--text-muted)' }}>{user?.role?.replace(/_/g, ' ')}</p>
              </div>
              <NotificationBell />
            </div>
          </div>
          <div />
        </header>

        {/* Mobile PWA header */}
        <header className="md:hidden sticky top-0 z-50 backdrop-blur-xl border-b"
          style={{ background: 'rgba(10, 13, 16, 0.97)', borderColor: 'var(--border)' }}>
          <div className="px-4 h-16 flex items-center justify-between">
            {/* Profile avatar - clickable to edit profile */}
            <button onClick={() => navigate('/ops/profile')}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-transform active:scale-95"
              style={{ background: 'var(--primary-muted)', border: '1px solid var(--primary)' }}
              data-testid="ops-profile-btn">
              <span className="text-xs font-extrabold font-['Manrope']" style={{ color: 'var(--primary)' }}>{initials}</span>
            </button>
            
            {/* Center logo and dropdown - positioned absolutely to center on screen */}
            <div className="absolute left-1/2 -translate-x-1/2" ref={mobileMenuRef}>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex flex-col items-center gap-0.5 active:scale-95 transition-transform"
                data-testid="ops-mobile-menu-trigger">
                <Logo size={48} />
                <div className="flex items-center gap-1">
                  <span className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{currentPage}</span>
                  <ChevronDown size={12} style={{ color: 'var(--text-faint)' }} className={`transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {mobileMenuOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 rounded-lg overflow-hidden animate-fade-in"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}
                  data-testid="ops-mobile-dropdown">
                  <div className="max-h-[70vh] overflow-y-auto p-3 space-y-1">
                    {/* Collapsible sections for mobile */}
                    {filteredGroups.map((group, gi) => (
                      <div key={gi} className="rounded-md overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)' }}>
                        <button
                          onClick={() => toggleSection(group.label)}
                          className="w-full flex items-center justify-between px-3 py-2.5 transition-colors"
                          style={{ color: 'var(--primary)' }}
                          data-testid={`mobile-section-${group.label.toLowerCase()}`}
                        >
                          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-widest">{group.label}</span>
                          <ChevronDown 
                            size={14} 
                            className={`transition-transform duration-200 ${expandedSections[group.label] ? '' : '-rotate-90'}`}
                          />
                        </button>
                        <div className={`overflow-hidden transition-all duration-200 ${expandedSections[group.label] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                          <div className="px-2 pb-2 space-y-0.5">
                            {group.items.map(({ to, icon: Icon, label }) => (
                              <NavLink key={to} to={to}
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center gap-2.5 px-3 py-2.5 rounded-md transition-all"
                                style={{ color: location.pathname.startsWith(to) ? 'var(--primary)' : 'var(--text-muted)', background: location.pathname.startsWith(to) ? 'var(--primary-muted)' : 'transparent' }}>
                                <Icon size={16} />
                                <span className="font-['IBM_Plex_Sans'] text-sm font-medium">{label}</span>
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 border-t" style={{ borderColor: 'var(--border)' }}>
                    <NavLink to="/ops/profile" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-1.5 text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>
                      <User size={14} /> Profile
                    </NavLink>
                    <div className="flex items-center gap-2">
                      <Radio size={10} className="live-dot" style={{ color: 'var(--success)' }} />
                      <span className="text-[10px] font-['JetBrains_Mono']" style={{ color: 'var(--success)' }}>ONLINE</span>
                    </div>
                    <button onClick={handleLogout} className="flex items-center gap-1.5 text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--error)' }}>
                      <LogOut size={14} /> Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* Sign out + Notification */}
            <div className="flex items-center gap-2">
              <button onClick={handleLogout}
                className="p-2 rounded-md transition-colors"
                style={{ color: 'var(--text-faint)' }}
                data-testid="ops-mobile-signout-btn">
                <LogOut size={18} />
              </button>
              <NotificationBell />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto page-padding" style={{ background: 'var(--bg)' }}>
          <Outlet />
        </main>
      </div>

      {/* ═══ DESKTOP SIDEBAR — RIGHT SIDE (hidden on mobile) ═══ */}
      <aside className={`${collapsed ? 'w-16' : 'w-60'} hidden md:flex flex-col border-l transition-all duration-300 flex-shrink-0`}
        style={{ background: 'var(--bg-darker)', borderColor: 'var(--border)' }}>
        <div className="h-16 flex items-center px-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <button onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded transition-colors hover:bg-[var(--surface)]"
            style={{ color: 'var(--text-muted)' }}>
            <ChevronRight size={18} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
          {!collapsed && <span className="ml-3 text-xs font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Menu</span>}
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {filteredGroups.map((group, gi) => (
            <div key={gi} className="px-2 mb-1">
              {/* Section Header - Clickable to expand/collapse */}
              {!collapsed && (
                <button
                  onClick={() => toggleSection(group.label)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-md text-xs font-['JetBrains_Mono'] uppercase tracking-wider transition-colors hover:bg-[var(--surface)]"
                  style={{ color: 'var(--text-faint)' }}
                  data-testid={`section-toggle-${group.label.toLowerCase()}`}
                >
                  <span>{group.label}</span>
                  <ChevronDown 
                    size={14} 
                    className={`transition-transform duration-200 ${expandedSections[group.label] ? '' : '-rotate-90'}`}
                    style={{ color: 'var(--text-faint)' }}
                  />
                </button>
              )}
              
              {/* Section Items - Collapsible */}
              <div className={`space-y-0.5 overflow-hidden transition-all duration-200 ${
                collapsed ? '' : (expandedSections[group.label] ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0')
              }`}>
                {group.items.map(item => (
                  <NavLink key={item.to} to={item.to}
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150"
                    style={({ isActive }) => ({
                      background: isActive ? 'var(--primary)' : 'transparent',
                      color: isActive ? 'white' : 'var(--text-primary)',
                      opacity: isActive ? 1 : 0.75,
                      boxShadow: isActive ? '0 0 16px rgba(59,130,246,0.25), inset -3px 0 8px rgba(59,130,246,0.2)' : 'none',
                    })}>
                    <item.icon size={18} className="flex-shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t" style={{ borderColor: 'var(--border)' }}>
          <div className={`mb-3 px-3 py-2 rounded-md ${collapsed ? 'hidden' : ''}`} style={{ background: 'var(--surface)' }}>
            <div className="flex items-center gap-2">
              <Radio size={12} className="live-dot" style={{ color: 'var(--success)' }} />
              <span className="text-xs font-medium" style={{ color: 'var(--success)' }}>SYSTEM ONLINE</span>
            </div>
            <p className="text-xs mt-1 font-mono" style={{ color: 'var(--text-faint)' }}>
              {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--error-muted)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <LogOut size={18} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>
    </div>
  );
}
